import { useEffect, useState, useMemo } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MDSnackbar from "components/MDSnackbar";

import {
  GetAllWithdrawalsCall,
  GetEarnersCall,
  GetEarnerSalesCall,
  UpdateWithdrawalStatusCall,
  UndoWithdrawalPayoffCall,
} from "../../redux/api/affiliation.api";

const cellSx = {
  py: 1.5,
  px: 2,
  fontSize: "0.8rem",
};

function EarnersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(true);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuEarner, setMenuEarner] = useState(null);

  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [salesUserId, setSalesUserId] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [payoffLoadingId, setPayoffLoadingId] = useState(null);

  const [pageWithdrawals, setPageWithdrawals] = useState(0);
  const [rowsPerPageWithdrawals] = useState(5);
  const [pageEarners, setPageEarners] = useState(0);
  const [rowsPerPageEarners] = useState(10);
  const [paidSuccessSB, setPaidSuccessSB] = useState(false);

  const fetchEarners = async () => {
    setLoading(true);
    const data = await GetEarnersCall();
    setRows(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const fetchAllWithdrawals = async () => {
    setWithdrawalsLoading(true);
    const data = await GetAllWithdrawalsCall();
    setAllWithdrawals(Array.isArray(data) ? data : []);
    setWithdrawalsLoading(false);
  };

  useEffect(() => {
    fetchEarners();
  }, []);

  useEffect(() => {
    fetchAllWithdrawals();
  }, []);

  useEffect(() => {
    if (!salesDialogOpen || !salesUserId) return;
    let mounted = true;
    setSalesLoading(true);
    GetEarnerSalesCall(salesUserId)
      .then((data) => {
        if (mounted && data) setSalesData(data);
      })
      .finally(() => {
        if (mounted) setSalesLoading(false);
      });
    return () => { mounted = false; };
  }, [salesDialogOpen, salesUserId]);

  const openMenu = (e, earner) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuEarner(earner);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuEarner(null);
  };

  const handleViewSalesProgress = () => {
    if (menuEarner) {
      setSalesUserId(menuEarner.id);
      setSalesData(null);
      setSalesDialogOpen(true);
    }
    closeMenu();
  };

  const closeSalesDialog = () => {
    setSalesDialogOpen(false);
    setSalesUserId(null);
    setSalesData(null);
  };

  const formatMoney = (n) => {
    if (n == null || n === undefined) return "—";
    return Number(n).toFixed(2);
  };

  const handleMarkAsPaid = async (withdrawalId) => {
    setPayoffLoadingId(withdrawalId);
    const res = await UpdateWithdrawalStatusCall(withdrawalId, "paid");
    setPayoffLoadingId(null);
    if (res) {
      setPaidSuccessSB(true);
      fetchAllWithdrawals();
      fetchEarners();
      if (salesUserId) {
        const fresh = await GetEarnerSalesCall(salesUserId);
        if (fresh) setSalesData(fresh);
      }
    }
  };

  const handleUndoPayoff = async (withdrawalId) => {
    setPayoffLoadingId(withdrawalId);
    const res = await UndoWithdrawalPayoffCall(withdrawalId);
    setPayoffLoadingId(null);
    if (res) {
      fetchAllWithdrawals();
      fetchEarners();
      if (salesUserId) {
        const fresh = await GetEarnerSalesCall(salesUserId);
        if (fresh) setSalesData(fresh);
      }
    }
  };

  const stats = useMemo(() => {
    const requested = allWithdrawals.filter(
      (w) => w.status && !["paid", "rejected"].includes(w.status)
    );
    const paid = allWithdrawals.filter((w) => w.status === "paid");
    const rejected = allWithdrawals.filter((w) => w.status === "rejected");
    const totalEarned = rows.reduce((s, r) => s + (Number(r.total_earned) || 0), 0);
    const totalPending = rows.reduce((s, r) => s + (Number(r.pending) || 0), 0);
    const requestedAmount = requested.reduce((s, w) => s + (Number(w.amount) || 0), 0);
    const paidAmount = paid.reduce((s, w) => s + (Number(w.amount) || 0), 0);
    return {
      requestedCount: requested.length,
      paidCount: paid.length,
      rejectedCount: rejected.length,
      totalEarned,
      totalPending,
      requestedAmount,
      paidAmount,
    };
  }, [allWithdrawals, rows]);

  const paginatedWithdrawals = useMemo(
    () =>
      allWithdrawals.slice(
        pageWithdrawals * rowsPerPageWithdrawals,
        pageWithdrawals * rowsPerPageWithdrawals + rowsPerPageWithdrawals
      ),
    [allWithdrawals, pageWithdrawals, rowsPerPageWithdrawals]
  );
  const paginatedEarners = useMemo(
    () =>
      rows.slice(
        pageEarners * rowsPerPageEarners,
        pageEarners * rowsPerPageEarners + rowsPerPageEarners
      ),
    [rows, pageEarners, rowsPerPageEarners]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Summary cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="warning"
              title="Requested"
              count={stats.requestedCount}
              percentage={{ color: "warning", amount: `€${stats.requestedAmount.toFixed(2)}`, label: "amount" }}
              icon="pending_actions"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="success"
              title="Paid"
              count={stats.paidCount}
              percentage={{ color: "success", amount: `€${stats.paidAmount.toFixed(2)}`, label: "amount" }}
              icon="check_circle"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="primary"
              title="Total earned"
              count={`€${stats.totalEarned.toFixed(2)}`}
              percentage={{ color: "primary", amount: rows.length, label: "earners" }}
              icon="trending_up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="info"
              title="Pending"
              count={`€${stats.totalPending.toFixed(2)}`}
              percentage={{ color: "warning", amount: "unpaid", label: "" }}
              icon="schedule"
            />
          </Grid>
        </Grid>

        {/* All withdrawal requests — everyone who requested money, payoff buttons here */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <Icon sx={{ color: ({ palette: { warning } }) => warning.main }}>request_quote</Icon>
              <MDTypography variant="h5" fontWeight="bold">
                Withdrawal requests (payoff)
              </MDTypography>
            </MDBox>
            <MDTypography variant="button" color="text" display="block" sx={{ mb: 2 }}>
              Everyone who requested a payout. Pay manually, then mark as paid to settle. Use Undo if marked by mistake.
            </MDTypography>
            {withdrawalsLoading ? (
              <MDTypography color="text">Loading…</MDTypography>
            ) : allWithdrawals.length === 0 ? (
              <MDTypography color="text.secondary">No withdrawal requests.</MDTypography>
            ) : (
              <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "background.paper" }}>
                      <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>User</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>Requested</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: "bold" }} align="right">Amount</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>Status</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: "bold" }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedWithdrawals.map((w) => {
                      const user = w.user || {};
                      const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "—";
                      return (
                        <TableRow key={w.id} hover>
                          <TableCell sx={cellSx}>
                            <MDTypography variant="button" fontWeight="medium">{name}</MDTypography>
                            {user.email && (
                              <MDTypography variant="caption" display="block" color="text.secondary">{user.email}</MDTypography>
                            )}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {w.created_at ? moment(w.created_at).format("MMM DD, YYYY HH:mm") : "—"}
                          </TableCell>
                          <TableCell sx={cellSx} align="right">{formatMoney(w.amount)}</TableCell>
                          <TableCell sx={cellSx}>
                            <Chip
                              label={String(w.status || "").replace(/_/g, " ")}
                              size="small"
                              color={w.status === "paid" ? "success" : w.status === "rejected" ? "error" : "warning"}
                            />
                          </TableCell>
                          <TableCell sx={cellSx} align="right">
                            {w.status === "paid" ? (
                              <Button
                                size="small"
                                startIcon={<UndoIcon />}
                                onClick={() => handleUndoPayoff(w.id)}
                                disabled={payoffLoadingId === w.id}
                              >
                                Undo
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleMarkAsPaid(w.id)}
                                disabled={payoffLoadingId === w.id}
                              >
                                Mark as paid
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={allWithdrawals.length}
                page={pageWithdrawals}
                onPageChange={(_, newPage) => setPageWithdrawals(newPage)}
                rowsPerPage={rowsPerPageWithdrawals}
                rowsPerPageOptions={[5]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
              />
            </>
            )}
          </MDBox>
        </Card>

        <Card sx={{ boxShadow: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <Icon sx={{ color: ({ palette: { primary } }) => primary.main }}>payments</Icon>
              <MDTypography variant="h5" fontWeight="bold">
                Earning users
              </MDTypography>
            </MDBox>
            <MDTypography variant="button" color="text">
              Users with active earning money tab. Use the 3-dots menu to view sales progress.
            </MDTypography>
          </MDBox>

          {loading ? (
            <MDBox p={3} textAlign="center">
              <MDTypography color="text">Loading…</MDTypography>
            </MDBox>
          ) : (
            <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "background.paper" }}>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }} align="right">Total earned</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }} align="center">Conversions</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }} align="right">Pending</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold" }}>Withdrawals</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: "bold", width: 56 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ ...cellSx, textAlign: "center", color: "text.secondary" }}>
                        No earning users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedEarners.map((earner) => (
                      <TableRow key={earner.id} hover>
                        <TableCell sx={cellSx}>
                          <MDTypography variant="button" fontWeight="medium">
                            {[earner.first_name, earner.last_name].filter(Boolean).join(" ") || "—"}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={cellSx}>{earner.email || "—"}</TableCell>
                        <TableCell sx={cellSx} align="right">{formatMoney(earner.total_earned)}</TableCell>
                        <TableCell sx={cellSx} align="center">{earner.conversions_count ?? 0}</TableCell>
                        <TableCell sx={cellSx} align="right">{formatMoney(earner.pending)}</TableCell>
                        <TableCell sx={cellSx}>
                          {((earner.withdrawals || []).length === 0)
                            ? "—"
                            : (earner.withdrawals || []).map((w) => (
                                <Chip
                                  key={w.id}
                                  label={`${formatMoney(w.amount)} ${String(w.status).replace(/_/g, " ")}`}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                  color={w.status === "paid" ? "success" : w.status === "rejected" ? "error" : "default"}
                                />
                              ))}
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <IconButton size="small" onClick={(e) => openMenu(e, earner)} aria-label="Actions">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              page={pageEarners}
              onPageChange={(_, newPage) => setPageEarners(newPage)}
              rowsPerPage={rowsPerPageEarners}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
            />
            </>
          )}
        </Card>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleViewSalesProgress}>View sales progress</MenuItem>
        </Menu>

        <Dialog open={salesDialogOpen} onClose={closeSalesDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Sales progress
            {salesData?.user && (
              <> — {[salesData.user.first_name, salesData.user.last_name].filter(Boolean).join(" ") || salesData.user.email}</>
            )}
          </DialogTitle>
          <DialogContent>
            {salesLoading ? (
              <MDTypography color="text">Loading…</MDTypography>
            ) : salesData ? (
              <>
                <MDBox mb={2} display="flex" gap={2} flexWrap="wrap">
                  <Chip label={`Total earned: ${formatMoney(salesData.total_earned)}`} color="primary" />
                  <Chip label={`Pending: ${formatMoney(salesData.pending)}`} color="warning" />
                </MDBox>

                {/* Withdrawal requests (payoff) — always show; settle when you pay manually */}
                <MDBox mb={3}>
                  <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Withdrawal requests (payoff)
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    When you pay the user manually, mark as paid to settle. Use Undo if marked by mistake.
                  </MDTypography>
                  {(salesData.withdrawals?.length ?? 0) === 0 ? (
                    <MDTypography variant="body2" color="text.secondary">
                      No withdrawal requests.
                    </MDTypography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={cellSx} fontWeight="bold">Requested</TableCell>
                            <TableCell sx={cellSx} fontWeight="bold" align="right">Amount</TableCell>
                            <TableCell sx={cellSx} fontWeight="bold">Status</TableCell>
                            <TableCell sx={cellSx} fontWeight="bold" align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salesData.withdrawals.map((w) => (
                            <TableRow key={w.id}>
                              <TableCell sx={cellSx}>
                                {w.created_at ? moment(w.created_at).format("MMM DD, YYYY HH:mm") : "—"}
                              </TableCell>
                              <TableCell sx={cellSx} align="right">{formatMoney(w.amount)}</TableCell>
                              <TableCell sx={cellSx}>
                                <Chip
                                  label={String(w.status).replace(/_/g, " ")}
                                  size="small"
                                  color={w.status === "paid" ? "success" : w.status === "rejected" ? "error" : "default"}
                                />
                              </TableCell>
                              <TableCell sx={cellSx} align="right">
                                {w.status === "paid" ? (
                                  <Button
                                    size="small"
                                    startIcon={<UndoIcon />}
                                    onClick={() => handleUndoPayoff(w.id)}
                                    disabled={payoffLoadingId === w.id}
                                  >
                                    Undo
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => handleMarkAsPaid(w.id)}
                                    disabled={payoffLoadingId === w.id}
                                  >
                                    Mark as paid
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </MDBox>

                <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Conversions
                </MDTypography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={cellSx} fontWeight="bold">Date</TableCell>
                        <TableCell sx={cellSx} fontWeight="bold">Package</TableCell>
                        <TableCell sx={cellSx} fontWeight="bold" align="right">Amount</TableCell>
                        <TableCell sx={cellSx} fontWeight="bold" align="center">Paid</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(salesData.conversions || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ ...cellSx, textAlign: "center", color: "text.secondary" }}>
                            No conversions yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        (salesData.conversions || []).map((c) => (
                          <TableRow key={c.id}>
                            <TableCell sx={cellSx}>{moment(c.created_at).format("MMM DD, YYYY HH:mm")}</TableCell>
                            <TableCell sx={cellSx}>
                              {c.conversionPackage?.name || c.conversionPackage?.identifier || `#${c.package_id}`}
                            </TableCell>
                            <TableCell sx={cellSx} align="right">{formatMoney(c.amount_earned)}</TableCell>
                            <TableCell sx={cellSx} align="center">
                              <Chip label={c.is_paid ? "Paid" : "Pending"} size="small" color={c.is_paid ? "success" : "warning"} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </MDBox>
      <Footer />
      <MDSnackbar
        color="success"
        icon="check_circle"
        title="Payment recorded"
        content="We've sent the money to the user's PayPal account."
        dateTime="Just now"
        open={paidSuccessSB}
        onClose={() => setPaidSuccessSB(false)}
        close={() => setPaidSuccessSB(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default EarnersPage;
